/**
 * DonationRecordsPanel — donations that came in through the public portal.
 *
 * Gifts land as `pending`: card payments are completed by the gateway, and
 * offline gifts (cash, bank transfer, mobile money) are completed here, by an
 * admin confirming the money actually arrived.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

export const DonationRecordsPanel = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: records = [], isLoading } = useQuery({
    queryKey: ['donation-records', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/donations/records`),
  });

  const confirmDonation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/donations/records/${id}/confirm`, { method: 'POST' }),
    onSuccess: (res) => {
      toast.success(res.already_completed
        ? 'Already confirmed — nothing was counted twice'
        : 'Confirmed · receipt sent and ledger posted');
      qc.invalidateQueries({ queryKey: ['donation-records', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const pending = records.filter((r) => r.status !== 'completed').length;

  return (
    <Card data-testid="donation-records-panel">
      <CardHeader>
        <CardTitle className="text-lg">Donation records</CardTitle>
        <CardDescription>
          {pending} awaiting confirmation · a gift only counts once the payment is confirmed
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Donor #</TableHead>
              <TableHead>Donor</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400">Loading…</TableCell></TableRow>
            )}
            {!isLoading && records.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400" data-testid="donation-records-empty">No donations yet</TableCell></TableRow>
            )}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`donation-row-${r.id}`}>
                <TableCell className="text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-xs">{r.donor_number || '—'}</Badge></TableCell>
                <TableCell className="text-sm">
                  {r.is_anonymous ? 'Anonymous' : (r.donor_name || '—')}
                  {r.donor_email && <p className="text-[10px] text-slate-400">{r.donor_email}</p>}
                </TableCell>
                <TableCell className="font-semibold text-emerald-700">
                  {(r.currency || 'USD')} {Number(r.amount || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-xs capitalize">{(r.payment_method || '—').replace(/_/g, ' ')}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge className={r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}
                           data-testid={`donation-status-${r.id}`}>
                      {r.status}
                    </Badge>
                    {r.status !== 'completed' && (
                      <Button variant="outline" size="sm" className="h-6 text-[10px]"
                              data-testid={`confirm-donation-${r.id}`}
                              disabled={confirmDonation.isPending}
                              onClick={() => confirmDonation.mutate(r.id)}>
                        Confirm receipt
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default DonationRecordsPanel;
